/**
 * `GET /event-fields/available?category=ACTIVITY|TRANSACTION` — stub.
 *
 * GENERATED FROM THE BACKEND ENUM, not hand-written: every entry is a real
 * `FraudEntityField` case object, with `technicalName` its snake_case `entryName`
 * and `displayName` its `description`. Source:
 *   be/fraud_common/src/main/scala/com/tookitaki/fraud_common/model/enums/FraudEntityField.scala
 *
 * WHY GENERATED. Three lookup tables in the Spatial View were keyed on plausible
 * but INVENTED event names and silently matched nothing for weeks. A hand-written
 * field list would repeat that mistake one layer up — an admin would configure
 * `auth_method`, and the node would render nothing with no error anywhere. Working
 * against the real keys means a mismatch shows up while building.
 *
 * `isIdentifier` is derived exactly as the service derives it: `activity_id` for
 * ACTIVITY, `transaction_id` for TRANSACTION. Story §13 — not removable, and its
 * technical name cannot be changed.
 *
 * REPLACE THIS with the live endpoint when it ships; the shape is already
 * `SchemaFieldEntry`, so only the service implementation changes.
 *
 * DE-DUPLICATED ON GENERATION. Two enum entries snake_case to `custom_data`
 * (descriptions "Custom Data" and "Custom data"), so the raw list contains it
 * twice. That matters beyond this fixture: the live endpoint would return the
 * duplicate too, the picker would show two identical-looking rows, and
 * `UNIQUE KEY uq_event_type_field (event_type_id, field_key)` would reject the
 * second on save. Raised with the BE 2026-08-25. `custom_data` is also the JSON
 * blob itself rather than a displayable value — arguably it should not be offered
 * at all.
 *
 * NOTE FOR THE FIELD PICKER: this list is CATEGORY-level, not per event type, and
 * it is flat — it mixes top-level columns (`activity_id`, `ip_address`) with nested
 * groups such as IntermediaryField, where `first_name` means the INTERMEDIARY's
 * first name. The design shows only the technical name beside the label, so those
 * read identically to a top-level field. Raised with the BE 2026-08-24.
 */

export const AVAILABLE_FIELDS = {
  ACTIVITY: [
    {
      technicalName: 'activity_id',
      displayName: 'Activity ID',
      isIdentifier: true
    },
    { technicalName: 'activity_datetime', displayName: 'Activity timestamp' },
    {
      technicalName: 'activity_datetime_year_month',
      displayName: 'Activity Year Month'
    },
    { technicalName: 'activity_type', displayName: 'Activity type' },
    { technicalName: 'user_id', displayName: 'User ID' },
    { technicalName: 'activity_subtype', displayName: 'Activity subtype' },
    {
      technicalName: 'activity_description',
      displayName: 'Activity description'
    },
    { technicalName: 'activity_status', displayName: 'Activity status' },
    { technicalName: 'custom_data', displayName: 'Custom data' },
    { technicalName: 'ip_address', displayName: 'IP address' },
    { technicalName: 'device_fingerprint', displayName: 'Device fingerprint' },
    { technicalName: 'hashcode', displayName: 'Intermediary Hashcode' },
    {
      technicalName: 'advance_hashcode',
      displayName: 'Intermediary Advance Hashcode'
    },
    { technicalName: 'first_name', displayName: 'Intermediary First Name' },
    { technicalName: 'last_name', displayName: 'Intermediary Last Name' },
    { technicalName: 'country', displayName: 'Intermediary Country' },
    { technicalName: 'state', displayName: 'Intermediary State' },
    { technicalName: 'city', displayName: 'Intermediary City' },
    { technicalName: 'address', displayName: 'Intermediary Address' },
    { technicalName: 'nationality', displayName: 'Intermediary Nationality' },
    { technicalName: 'msisdn', displayName: 'Intermediary MSISDN' },
    {
      technicalName: 'incorporation_date',
      displayName: 'Intermediary Incorporation Date'
    },
    { technicalName: 'partner', displayName: 'Intermediary Partner' },
    { technicalName: 'partner_code', displayName: 'Intermediary Partner Code' },
    { technicalName: 'partner_type', displayName: 'Intermediary Partner Type' },
    { technicalName: 'payer_name', displayName: 'Intermediary Payer Name' },
    {
      technicalName: 'payer_service',
      displayName: 'Intermediary Payer Service'
    },
    {
      technicalName: 'payer_country',
      displayName: 'Intermediary Payer Country'
    },
    {
      technicalName: 'device_type',
      displayName: 'Device Type (e.g., smartphone, tablet, laptop, desktop)'
    },
    { technicalName: 'manufacturer', displayName: 'Manufacturer' },
    { technicalName: 'model', displayName: 'Model' },
    { technicalName: 'operating_system', displayName: 'Operating System' },
    { technicalName: 'os_version', displayName: 'OS Version' },
    { technicalName: 'browser', displayName: 'Browser' },
    {
      technicalName: 'imei_meid',
      displayName: 'IMEI/MEID (for mobile devices)'
    },
    { technicalName: 'mac_address', displayName: 'MAC Address' },
    { technicalName: 'date', displayName: 'Date' }
  ],

  TRANSACTION: [
    {
      technicalName: 'transaction_id',
      displayName: 'Transaction Number',
      isIdentifier: true
    },
    { technicalName: 'txn_date_time', displayName: 'Transaction Timestamp' },
    {
      technicalName: 'txn_date_time_year_month',
      displayName: 'Transaction Year Month'
    },
    { technicalName: 'txn_type_code', displayName: 'Business Type' },
    { technicalName: 'txn_type', displayName: 'Transaction Type' },
    {
      technicalName: 'txn_geography_risk',
      displayName: 'Risk of the geography where the transaction takes place'
    },
    { technicalName: 'txn_status', displayName: 'Status of the transaction' },
    {
      technicalName: 'purpose_code',
      displayName: 'Purpose of the transaction'
    },
    {
      technicalName: 'sender_amount_usd',
      displayName: 'Amount sent by the Sender in USD'
    },
    {
      technicalName: 'sender_amount',
      displayName: 'Amount sent by the Sender'
    },
    { technicalName: 'sender_currency', displayName: 'Currency of the Sender' },
    { technicalName: 'sender_country', displayName: 'Country of the Sender' },
    {
      technicalName: 'sender_country_risk_level',
      displayName: 'Risk level for the Sender country'
    },
    {
      technicalName: 'receiver_country',
      displayName: 'Country of the Receiver'
    },
    {
      technicalName: 'receiver_country_risk_level',
      displayName: 'Risk level for the Receiver country'
    },
    {
      technicalName: 'receiver_amount',
      displayName: 'Amount Received by the Receiver'
    },
    { technicalName: 'receiver_currency', displayName: 'Receiver Currency' },
    { technicalName: 'credit_party', displayName: 'Credit Party' },
    {
      technicalName: 'sending_partner',
      displayName: 'Partner ID for the sender'
    },
    {
      technicalName: 'sending_partner_code',
      displayName: 'Partner code for the sender'
    },
    {
      technicalName: 'sending_partner_type',
      displayName: 'Partner type for the sender'
    },
    {
      technicalName: 'sending_partner_risk_level',
      displayName: 'Partner risk level for the sender'
    },
    {
      technicalName: 'receiving_partner',
      displayName: 'Partner ID for the Receiver'
    },
    {
      technicalName: 'receiving_partner_code',
      displayName: 'Partner code for the Receiver'
    },
    {
      technicalName: 'receiving_partner_type',
      displayName: 'Receiver Partner Type'
    },
    {
      technicalName: 'receiving_partner_risk_level',
      displayName: 'Payer partner Risk level for the receiver'
    },
    { technicalName: 'sender_hashcode', displayName: 'Sender Identifier' },
    {
      technicalName: 'sender_advance_hashcode',
      displayName: 'Sender Identifier Advanced'
    },
    { technicalName: 'sender_first_name', displayName: 'Sender First Name' },
    { technicalName: 'sender_last_name', displayName: 'Sender Last Name' },
    {
      technicalName: 'sender_birth_country',
      displayName: 'Birth country of the Sender'
    },
    { technicalName: 'sender_state', displayName: 'Sender State' },
    { technicalName: 'sender_city', displayName: 'City of the Sender' },
    { technicalName: 'sender_address', displayName: 'Sender Address' },
    { technicalName: 'sender_postal_code', displayName: 'Sender Postal Code' },
    { technicalName: 'sender_occupation', displayName: 'Sender Occupation' },
    { technicalName: 'sender_nationality', displayName: 'Sender Nationality' },
    { technicalName: 'sender_msisdn', displayName: 'Sender Phone Number' },
    {
      technicalName: 'sender_id_number',
      displayName: 'Sender Identity Number'
    },
    { technicalName: 'sender_id_type', displayName: 'Sender Identity Type' },
    {
      technicalName: 'sender_incorporation_date',
      displayName: 'Sender Incorporation Date'
    },
    { technicalName: 'sender_gender', displayName: 'Sender Gender' },
    {
      technicalName: 'sender_identifier_hashcode',
      displayName: 'Sender Identifier Hashcode'
    },
    { technicalName: 'sender_type', displayName: 'Sender Type' },
    { technicalName: 'receiver_hashcode', displayName: 'Receiver Identifier' },
    {
      technicalName: 'receiver_advance_hashcode',
      displayName: 'Receiver Identifier Advanced'
    },
    {
      technicalName: 'receiver_first_name',
      displayName: 'Receiver First Name'
    },
    { technicalName: 'receiver_last_name', displayName: 'Receiver Last Name' },
    {
      technicalName: 'receiver_birth_country',
      displayName: 'Receiver country of birth'
    },
    { technicalName: 'receiver_state', displayName: 'Receiver State' },
    { technicalName: 'receiver_city', displayName: 'Receiver City' },
    { technicalName: 'receiver_address', displayName: 'Receiver Address' },
    {
      technicalName: 'receiver_postal_code',
      displayName: 'Receiver Postal Code'
    },
    {
      technicalName: 'receiver_occupation',
      displayName: 'Receiver Occupation'
    },
    {
      technicalName: 'receiver_nationality',
      displayName: 'Receiver Nationality'
    },
    { technicalName: 'receiver_msisdn', displayName: 'Receiver Phone Number' },
    {
      technicalName: 'receiver_id_number',
      displayName: 'Receiver Identity Number'
    },
    {
      technicalName: 'receiver_id_type',
      displayName: 'Receiver Identity Type'
    },
    {
      technicalName: 'receiver_incorporation_date',
      displayName: 'Receiver Incorporation Date'
    },
    { technicalName: 'receiver_gender', displayName: 'Gender of the Receiver' },
    {
      technicalName: 'receiver_identifier_hashcode',
      displayName: 'Receiver Identifier Hashcode'
    },
    { technicalName: 'receiver_type', displayName: 'Receiver Type' },
    {
      technicalName: 'receiver_payer_name',
      displayName: 'Payer partner Name for the receiver'
    },
    {
      technicalName: 'receiver_payer_service',
      displayName: 'Payer service used by the Receiver'
    },
    {
      technicalName: 'receiver_payer_country',
      displayName: 'Country of the Payer partner for the receiver'
    },
    {
      technicalName: 'intermediary',
      displayName: 'List of intermediaries involved in the transaction'
    },
    {
      technicalName: 'additional_info',
      displayName: 'Additional information about the transaction'
    },
    {
      technicalName: 'aggregated_transaction',
      displayName: 'Is Transaction Aggregated?'
    },
    { technicalName: 'rfi_sent', displayName: 'Is RFI Sent?' },
    {
      technicalName: 'sender_receiver_relationship',
      displayName: 'Relationship between Sender and Receiver'
    },
    { technicalName: 'source_of_funds', displayName: 'Source of Funds' },
    {
      technicalName: 'document_reference_number',
      displayName: 'Document Reference Number'
    },
    { technicalName: 'external_id', displayName: 'External ID' },
    { technicalName: 'external_code', displayName: 'External Code' },
    { technicalName: 'custom_data', displayName: 'Custom Data' },
    {
      technicalName: 'txn_date_time_completion',
      displayName: 'Transaction completion timestamp'
    },
    { technicalName: 'sender_bic_code', displayName: 'Sender BIC code' },
    { technicalName: 'receiver_bic_code', displayName: 'Receiver BIC code' },
    { technicalName: 'account_id', displayName: 'Account Id' },
    { technicalName: 'account_currency', displayName: 'Account Currency' },
    { technicalName: 'atm_id', displayName: 'ATM Id' },
    { technicalName: 'teller_id', displayName: 'Teller Id' },
    { technicalName: 'merchant_name', displayName: 'Merchant Name' },
    { technicalName: 'credit_card_number', displayName: 'Credit Card Number' },
    { technicalName: 'product_code', displayName: 'Product Code' },
    { technicalName: 'swift_msg_type', displayName: 'SWIFT Message Type' },
    { technicalName: 'swift_msg_info', displayName: 'SWIFT Message Info' },
    { technicalName: 'tranche_no', displayName: 'Tranche No' },
    {
      technicalName: 'opp_citizenship_country',
      displayName: 'Counterparty Citizenship Country'
    },
    { technicalName: 'counterparty_type', displayName: 'Counterparty Type' },
    { technicalName: 'bank_branch_code', displayName: 'Bank Branch Code' },
    {
      technicalName: 'beneficial_bank_name',
      displayName: 'Beneficial Bank Name'
    },
    {
      technicalName: 'originator_bank_name',
      displayName: 'Originator Bank Name'
    },
    {
      technicalName: 'refund_to_third_party_flag',
      displayName: 'Refund To Third Party Flag'
    },
    { technicalName: 'ip_riskiness', displayName: 'IP Riskiness' },
    {
      technicalName: 'remittance_payment_details',
      displayName: 'Remittance Payment Details'
    },
    {
      technicalName: 'originator_bank_country_code',
      displayName: 'Originator Bank Country Code'
    },
    {
      technicalName: 'beneficiary_bank_country_code',
      displayName: 'Beneficiary Bank Country Code'
    },
    { technicalName: 'atm_location', displayName: 'Atm Location' },
    { technicalName: 'activity_id', displayName: 'Activity ID' },
    { technicalName: 'activity_datetime', displayName: 'Activity timestamp' },
    {
      technicalName: 'activity_datetime_year_month',
      displayName: 'Activity Year Month'
    },
    { technicalName: 'activity_type', displayName: 'Activity type' },
    { technicalName: 'user_id', displayName: 'User ID' },
    { technicalName: 'activity_subtype', displayName: 'Activity subtype' },
    {
      technicalName: 'activity_description',
      displayName: 'Activity description'
    },
    { technicalName: 'activity_status', displayName: 'Activity status' },
    { technicalName: 'ip_address', displayName: 'IP address' },
    { technicalName: 'device_fingerprint', displayName: 'Device fingerprint' },
    { technicalName: 'hashcode', displayName: 'Intermediary Hashcode' },
    {
      technicalName: 'advance_hashcode',
      displayName: 'Intermediary Advance Hashcode'
    },
    { technicalName: 'first_name', displayName: 'Intermediary First Name' },
    { technicalName: 'last_name', displayName: 'Intermediary Last Name' },
    { technicalName: 'country', displayName: 'Intermediary Country' },
    { technicalName: 'state', displayName: 'Intermediary State' },
    { technicalName: 'city', displayName: 'Intermediary City' },
    { technicalName: 'address', displayName: 'Intermediary Address' },
    { technicalName: 'nationality', displayName: 'Intermediary Nationality' },
    { technicalName: 'msisdn', displayName: 'Intermediary MSISDN' },
    {
      technicalName: 'incorporation_date',
      displayName: 'Intermediary Incorporation Date'
    },
    { technicalName: 'partner', displayName: 'Intermediary Partner' },
    { technicalName: 'partner_code', displayName: 'Intermediary Partner Code' },
    { technicalName: 'partner_type', displayName: 'Intermediary Partner Type' },
    { technicalName: 'payer_name', displayName: 'Intermediary Payer Name' },
    {
      technicalName: 'payer_service',
      displayName: 'Intermediary Payer Service'
    },
    {
      technicalName: 'payer_country',
      displayName: 'Intermediary Payer Country'
    },
    {
      technicalName: 'device_type',
      displayName: 'Device Type (e.g., smartphone, tablet, laptop, desktop)'
    },
    { technicalName: 'manufacturer', displayName: 'Manufacturer' },
    { technicalName: 'model', displayName: 'Model' },
    { technicalName: 'operating_system', displayName: 'Operating System' },
    { technicalName: 'os_version', displayName: 'OS Version' },
    { technicalName: 'browser', displayName: 'Browser' },
    {
      technicalName: 'imei_meid',
      displayName: 'IMEI/MEID (for mobile devices)'
    },
    { technicalName: 'mac_address', displayName: 'MAC Address' },
    { technicalName: 'date', displayName: 'Date' }
  ]
};

export default AVAILABLE_FIELDS;
